import { DragEvent, useCallback, useEffect, useRef, useState } from 'react'
import Button from '@/shared/Buttons/Button'
import DropArea from '@/shared/Files/Droparea'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import alertService from '@/services/alerts/alert.service'
import confirmService from '@/services/ConfirmService/confirm.service'
import ExternalLinkIcon from '@/assets/icons/externalLink.svg?react'
import PdfIcon from '@/assets/icons/icon-pdf-file.svg?react'
import TrashIcon from '@/assets/icons/trash.svg?react'
import UploadIcon from '@/assets/icons/upload.svg?react'
import cmsApi from '../cms.api'
import { IEventProgramStatus } from '../cms.types'
import { getAbsoluteCmsUrl } from '../cms.utils'
import css from '../cms.module.css'

interface IProps {
  initialStatus: IEventProgramStatus
  onStatusChange: (status: IEventProgramStatus) => void
}

const getProgramFileName = (url?: string | null) => {
  if (!url) return 'event-program.pdf'

  const rawFileName = url.split('/').pop() || 'event-program.pdf'

  try {
    return decodeURIComponent(rawFileName)
  } catch {
    return rawFileName
  }
}

const isPdf = (file: File) => {
  const hasPdfExtension = file.name.toLowerCase().endsWith('.pdf')
  const hasPdfType = file.type === '' || file.type === 'application/pdf' || file.type === 'application/x-pdf'

  return hasPdfExtension && hasPdfType
}

const EventProgramWorkspace = ({ initialStatus, onStatusChange }: IProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<IEventProgramStatus>(initialStatus)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  useEffect(() => {
    setStatus(initialStatus)
  }, [initialStatus])

  const syncStatus = useCallback(
    (nextStatus: IEventProgramStatus) => {
      setStatus(nextStatus)
      onStatusChange(nextStatus)
    },
    [onStatusChange]
  )

  const load = useCallback(async () => {
    setLoading(true)
    const response = await cmsApi.getEventProgram()
    setLoading(false)

    if (response.status === 'success') {
      syncStatus(response.body)
      return
    }

    alertService.requestAlert({ type: 'error', title: 'Не удалось загрузить программу', description: response.message })
  }, [syncStatus])

  useEffect(() => {
    load()
  }, [load])

  const upload = useCallback(
    async (file?: File) => {
      if (!file) return

      if (!isPdf(file)) {
        alertService.requestAlert({
          type: 'warning',
          title: 'Файл не загружен',
          description: 'Можно загрузить только PDF',
        })
        return
      }

      setUploading(true)
      const response = await cmsApi.uploadEventProgram(file)
      setUploading(false)

      if (response.status === 'error') {
        alertService.requestAlert({
          type: 'error',
          title: 'Не удалось загрузить программу',
          description: response.message,
        })
        return
      }

      syncStatus(response.body)
      alertService.requestAlert({ type: 'success', title: 'Программа загружена', description: file.name })
    },
    [syncStatus]
  )

  const reset = useCallback(async () => {
    if (!status.exists) return

    const confirmation = await confirmService.requestConfirm({
      title: 'Сбросить программу',
      description: 'Текущий PDF файл будет удален из программы мероприятия',
      confirmText: 'Сбросить',
      isDanger: true,
    })

    if (confirmation.response !== 'confirm') return

    setResetting(true)
    const response = await cmsApi.resetEventProgram()
    setResetting(false)

    if (response.status === 'error') {
      alertService.requestAlert({
        type: 'error',
        title: 'Не удалось сбросить программу',
        description: response.message,
      })
      return
    }

    syncStatus(response.body)
    alertService.requestAlert({ type: 'success', title: 'Программа сброшена' })
  }, [status.exists, syncStatus])

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragActive(false)
    upload(event.dataTransfer.files?.[0])
  }

  const programUrl = getAbsoluteCmsUrl(status.url || undefined)
  const fileName = getProgramFileName(status.url)

  return (
    <>
      <div className={css.toolbar}>
        <div>
          <h1 className="text-heading-4">Программа мероприятия</h1>
          <p className="text-nm text-description">{status.exists ? 'PDF загружен' : 'PDF не загружен'}</p>
        </div>
        <div className="inline-flex-gap gap8 center">
          {(loading || uploading || resetting) && <ThreeDotsLoader />}
          <Button
            size="default"
            type="default"
            text="Сбросить"
            icon={<TrashIcon />}
            danger
            disabled={!status.exists || uploading || resetting}
            loading={resetting}
            onClick={reset}
          />
          <Button
            size="default"
            type="primary"
            text="Загрузить PDF"
            icon={<UploadIcon />}
            loading={uploading}
            disabled={resetting}
            onClick={() => inputRef.current?.click()}
          />
        </div>
      </div>

      <div className={css.tablePanel}>
        <DropArea
          isActive={dragActive}
          onDrop={handleDrop}
          onDragEnter={(event) => {
            event.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={() => setDragActive(false)}
          onDragOver={(event) => event.preventDefault()}
          style={{ height: '100%' }}
        >
          <div className={css.eventProgramSurface}>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(event) => {
                upload(event.target.files?.[0])
                event.target.value = ''
              }}
            />

            <div className={css.eventProgramCard} data-empty={!status.exists}>
              <div className={css.eventProgramIcon}>
                <PdfIcon />
              </div>
              <div className={css.eventProgramMeta}>
                <p className="text-nm-bold line-clamp" title={status.exists ? fileName : 'Файл не выбран'}>
                  {status.exists ? fileName : 'Файл не выбран'}
                </p>
                <p className="text-sm text-description">
                  {status.exists ? 'Текущая программа мероприятия' : 'Загрузите PDF файл'}
                </p>
              </div>
              <Button
                size="default"
                type="default"
                text="Открыть"
                icon={<ExternalLinkIcon />}
                disabled={!programUrl}
                onClick={() => window.open(programUrl, '_blank', 'noopener,noreferrer')}
              />
            </div>
          </div>
        </DropArea>
      </div>
    </>
  )
}

export default EventProgramWorkspace
